import { PLAYER_IDS, type PlayerId } from './ids';
import type { BuildingTypeId } from './buildings/definitions';

/** One participant in a match. */
export interface MatchPlayer {
  readonly id: PlayerId;
  readonly name: string;
  /** Index into the map's player slots. */
  readonly slot: number;
  readonly team: number;
  /** Human on this device, remote human (later), or an empty seat. No AI exists yet. */
  readonly control: 'local' | 'remote' | 'none';
}

/** Structures every player starts with, centred on their slot. */
export interface StartingBuilding {
  readonly type: BuildingTypeId;
}

export interface MatchSetup {
  readonly players: readonly MatchPlayer[];
  readonly startingBuildings: readonly StartingBuilding[];
}

export const DEFAULT_STARTING_BUILDINGS: readonly StartingBuilding[] = [{ type: 'command_center' }];

/**
 * Local sandbox setup for Phase 2: player 1 is controlled on this device; any
 * other players are seats with a colony but NO controller (no AI until a
 * later phase). Each player is on their own team (free-for-all).
 */
export function localSandboxSetup(playerCount: number): MatchSetup {
  const n = Math.max(1, Math.min(PLAYER_IDS.length, Math.floor(playerCount)));
  const players: MatchPlayer[] = [];
  for (let i = 0; i < n; i++) {
    players.push({ id: PLAYER_IDS[i]!, name: i === 0 ? 'You' : `Rival ${i}`, slot: i, team: i, control: i === 0 ? 'local' : 'none' });
  }
  return { players, startingBuildings: DEFAULT_STARTING_BUILDINGS };
}

/**
 * Match setup for a mode (Phase 9): `count` seats; player 1 is local, the rest
 * remote (`remote = true`, online) or empty seats. 2v2 = p1 + p3 against p2 + p4
 * (slots 0/2 and 1/3, so allies start on the same side of the map).
 */
export function modeSetup(count: number, teams: 'ffa' | '2v2', remote = false): MatchSetup {
  const n = Math.max(1, Math.min(PLAYER_IDS.length, Math.floor(count)));
  const players: MatchPlayer[] = [];
  for (let i = 0; i < n; i++) {
    players.push({
      id: PLAYER_IDS[i]!,
      name: i === 0 ? 'You' : remote ? `Player ${i + 1}` : `Rival ${i}`,
      slot: i,
      team: teams === '2v2' ? i % 2 : i,
      control: i === 0 ? 'local' : remote ? 'remote' : 'none',
    });
  }
  return { players, startingBuildings: DEFAULT_STARTING_BUILDINGS };
}
